// 카메라 카드 아랫줄에 쓰는 "지점 / 팀".
//
// 실시간 관제·대시보드 미리보기·우선 미리보기가 같은 형식이어야 해서 한 곳에 모았다.
// 파일마다 같은 규칙을 복사해 두면 한쪽만 고쳐져 반드시 어긋난다.
//
// 팀이 배정되지 않은 사람이 있어 빈 칸이 생긴다. 그대로 이어 붙이면
// "평택 / " 처럼 슬래시가 매달려 고장난 것처럼 읽히므로, 있는 값만 잇는다.
// 지점조차 없는 회사(지점을 안 쓰는 곳)는 빈 문자열을 돌려주고,
// 호출부에서 그 화면의 기존 표기로 돌아간다.
//
// 값의 출처가 화면마다 달라(소스 객체 / statusMap 메타) 두 이름을 다 받는다.
export function getOwnerLine(source) {
  const branch = source?.ownerBranchName || source?.branchName || "";
  const team = source?.ownerTeamName || source?.teamName || "";

  return [branch, team].filter(Boolean).join(" / ");
}

// "지점 / 팀 / 작업자 / 차량번호" 네 토막.
// 이벤트 내역 카드와 프리뷰 표시 설정 목록이 같이 쓴다.
//
// 네 토막이라 빈 값이 하나만 있어도 "평택 / 1팀 / 김철수 / " 처럼 끝이 매달리거나
// " / / 김철수" 처럼 앞이 빈다. 실측(PPE 이벤트 577건)으로 네 칸이 모두 찬 건
// 20.6% 뿐이었다 — 차량번호가 119건으로 병목이다. 그래서 여기서도 있는 값만 잇는다.
//
// 전부 비면 빈 문자열을 돌려주고, 호출부가 기존 표기(message)로 돌아간다.
// (카메라 소유자를 못 찾는 이벤트가 577건 중 34건 있다)
export function getEventOwnerLine(row) {
  // 값의 출처가 화면마다 달라 이름이 두 가지다 — getOwnerLine 과 같은 이유.
  //   이벤트 행: branchName / teamName / workerName / vehicleNo
  //   카메라 행: ownerBranchName / ownerTeamName / ownerUserName / ownerVehicleNo (다시보기·실시간)
  // 한쪽만 읽으면 다른 화면에서 통째로 빈 문자열이 나온다.
  const branch = String(row?.ownerBranchName || row?.branchName || "").trim();
  const team = String(row?.ownerTeamName || row?.teamName || "").trim();
  const worker = String(row?.ownerUserName || row?.workerName || "").trim();
  const vehicle = String(row?.ownerVehicleNo || row?.vehicleNo || "").trim();

  // 카메라 이름(CamTitle)에는 관리자가 손으로 적어둔 "김성현 / 123마4567" 같은 라벨이 있다.
  // DB 차량번호는 4명뿐인데 손입력 라벨은 11건이라, 이걸 버리면 화면에 있던
  // 차량번호가 사라진다. 이름 필드는 화면마다 달라 네 가지를 다 본다.
  const title = String(
    row?.camName || row?.name || row?.camTitle || row?.deviceName || ""
  ).trim();

  // 신원 부분을 고른다. 원칙은 "화면에 있던 정보를 잃지 않는다".
  //   1) DB 차량번호가 있으면 DB 를 쓴다 — 앞으로의 정답이고 계정과 일치한다.
  //   2) 없고 카메라 이름에 "/" 가 있으면 손입력 라벨이므로 그대로 살린다.
  //      (그 계정들은 UserName 이 "평택 작업자5" 같은 임시값이라 DB 를 쓰면 오히려 나빠진다)
  //   3) 둘 다 아니면 DB 이름만. 자동 생성 이름("김민재 기본 카메라")은 군더더기라 안 쓴다.
  let identity;
  if (vehicle) identity = [worker, vehicle].filter(Boolean).join(" / ");
  else if (title.includes("/")) identity = title;
  else identity = worker;

  return [branch, team, identity].filter(Boolean).join(" / ");
}

// 이벤트 내역 카드에 보여줄 한 줄 — 위 네 토막 + 사건 설명.
//
// message 는 백엔드가 `${장비명} ${사건설명}` 으로 만든다(예: "김철수 / 123가4567 안전대 미착용 감지").
// 소유자 정보가 장비명 자리를 대신하므로, 앞의 장비명만 떼어내고 설명을 남긴다.
// 그냥 이어 붙이면 이름이 두 번 나온다.
//
// 배지(log.type)는 "⚠️ 안전장비 미착용" 까지만 알려줘서 안전모인지 안전대인지 구분이 안 된다.
// 그래서 설명을 지우면 안 된다.
//
// 소유자를 못 찾는 이벤트(577건 중 34건)는 원래 message 를 그대로 쓴다.
export function getEventCardText(row) {
  const message = String(row?.message || "").trim();
  const owner = getEventOwnerLine(row);
  if (!owner) return message;

  const deviceName = String(row?.deviceName || "").trim();
  const detail =
    deviceName && message.startsWith(deviceName)
      ? message.slice(deviceName.length).trim()
      : message;

  return [owner, detail].filter(Boolean).join(" ");
}
